import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import type { LanguageFieldFormValues } from "../types";

const PREAMBLE_PLACEHOLDER =
  "Text to be added to the document before all values";
const POSTAMBLE_PLACEHOLDER =
  "Text to be added to the document after all values";

export interface LanguageFieldFormProps {
  id: string;
  initialValues: LanguageFieldFormValues;
  isSubmitting: boolean;
  onSubmit: (values: LanguageFieldFormValues) => void;
  // Server-derived "Service Description" key, shown read-only on Edit. Omitted on New.
  sowLanguage?: string;
}

export const LanguageFieldForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
  sowLanguage,
}: LanguageFieldFormProps): JSX.Element => {
  const [values, setValues] = useState<LanguageFieldFormValues>(initialValues);

  const set = <K extends keyof LanguageFieldFormValues>(
    key: K,
    v: LanguageFieldFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="language-field-name">Name</Form.Label>
          <TextField
            label={null}
            id="language-field-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        {sowLanguage !== undefined && (
          <div className="col-sm-6">
            <Form.Label>Service Description</Form.Label>
            <div className="text-muted">{sowLanguage}</div>
            <small className="small text-muted d-block">
              Auto-generated from the name; used to reference this field in
              merge data.
            </small>
          </div>
        )}
      </div>

      <div className="row" style={{ paddingTop: "16px" }}>
        <div className="col">
          <Form.Label>Introduction</Form.Label>
          <MarkdownTextAreaV2
            alwaysEdit
            value={values.preamble}
            placeholder={PREAMBLE_PLACEHOLDER}
            onChange={(md: string) => set("preamble", md)}
          />
        </div>
      </div>

      <div className="row" style={{ paddingTop: "16px" }}>
        <div className="col">
          <Form.Label>Conclusion</Form.Label>
          <MarkdownTextAreaV2
            alwaysEdit
            value={values.postamble}
            placeholder={POSTAMBLE_PLACEHOLDER}
            onChange={(md: string) => set("postamble", md)}
          />
        </div>
      </div>
    </Form>
  );
};

export default LanguageFieldForm;
